import React from 'react';
import styled from 'styled-components';
import MedExplainLogo from './MedExplain_logo.png';

const PageContainer = styled.div`
  width: 100%;
  background: #ffffff;
  margin-bottom: 80px;
`;

const HeroSection = styled.section`
  padding: 80px 100px 60px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  color: white;
  text-align: center;

  h1 {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 20px;
    color:white;
  }

  p {
    font-size: 20px;
    max-width: 700px;
    margin: 0 auto;
    color: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 768px) {
    padding: 60px 20px 40px;
    h1 { font-size: 32px; }
    p { font-size: 18px; }
  }
`;

const CaseStudiesSection = styled.section`
  padding: 80px 100px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const CaseStudy = styled.div`
  background: white;
  border-radius: 12px;
  padding: 60px;
  margin-bottom: 60px;
  border: 2px solid #f0f0f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const CaseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  margin-bottom: 40px;

  img {
    max-width: 200px;
    height: auto;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;

    img {
      max-width: 150px;
    }
  }
`;

const CaseTitle = styled.div`
  h2 {
    font-size: 32px;
    color: #453793;
    margin-bottom: 10px;
    font-weight: 700;
  }

  .client {
    font-size: 16px;
    color: #8ecf41;
    font-weight: 600;
    margin-bottom: 5px;
  }

  .role {
    font-size: 14px;
    color: #666;
  }
`;

const CaseSection = styled.div`
  margin-bottom: 30px;

  h3 {
    font-size: 22px;
    color: #333;
    margin-bottom: 15px;
    font-weight: 600;
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #555;
    margin-bottom: 15px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    padding: 8px 0;
    padding-left: 25px;
    position: relative;
    color: #555;
    line-height: 1.6;

    &:before {
      content: "•";
      position: absolute;
      left: 0;
      color: #8ecf41;
      font-weight: bold;
      font-size: 20px;
    }
  }
`;

const ViewCaseStudyButton = styled.a`
  display: inline-block;
  padding: 12px 24px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  margin-top: 20px;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(69, 55, 147, 0.3);
    color: white;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 30px;
`;

const Metric = styled.div`
  background: #f8f9fa;
  padding: 25px;
  border-radius: 8px;
  text-align: center;
  border-left: 4px solid #8ecf41;

  .number {
    font-size: 32px;
    font-weight: 700;
    color: #453793;
    margin-bottom: 8px;
  }

  .label {
    font-size: 14px;
    color: #666;
  }
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 15px;

  .tech {
    padding: 8px 16px;
    background: #f0f0f0;
    border-radius: 6px;
    font-size: 13px;
    color: #555;
    font-weight: 500;
  }
`;

const TestimonialsSection = styled.section`
  padding: 80px 100px;
  background: #f8f9fa;

  h2 {
    font-size: 36px;
    text-align: center;
    color: #333;
    margin-bottom: 60px;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const Testimonial = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  margin-bottom: 40px;
  border-left: 5px solid #453793;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  .quote {
    font-size: 18px;
    line-height: 1.8;
    color: #555;
    font-style: italic;
    margin-bottom: 20px;
    white-space: pre-line;
  }

  .author {
    font-size: 16px;
    color: #453793;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 25px 20px;

    .quote {
      font-size: 16px;
    }
  }
`;

function CaseStudies() {
    return (
        <PageContainer>
            {/* HERO */}
            <HeroSection>
                <h1>Case Studies</h1>
                <p>Real projects, real results. See how we've helped companies scale their engineering.</p>
            </HeroSection>

            {/* CASE STUDIES */}
            <CaseStudiesSection>
                {/* MEDEXPLAIN */}
                <CaseStudy>
                    <CaseHeader>
                        <img src={MedExplainLogo} alt="MedExplain Health" />
                        <CaseTitle>
                            <div className="client">MedExplain Health</div>
                            <h2>Multilingual Health Education Platform</h2>
                            <div className="role">Fractional CTO & Solo Architect | Oct 2020 - Present</div>
                        </CaseTitle>
                    </CaseHeader>

                    <CaseSection>
                        <h3>The Challenge</h3>
                        <p>
                            Medical information was only accessible in English, creating health equity gaps for Spanish-speaking
                            patients. Manual translation was slow (weeks) and expensive. Dr. Monika Safford needed a scalable platform to deliver multilingual health education.
                        </p>
                    </CaseSection>

                    <CaseSection>
                        <h3>The Solution</h3>
                        <p>
                            Built complete enterprise content management platform from scratch as sole technical architect:
                        </p>
                        <ul>
                            <li>
                                <strong>AI-powered translation system</strong> using Azure AI Services - one-click English→Spanish
                                with clinical review workflow
                            </li>
                            <li>
                                <strong>Custom admin CMS</strong> with MSAL authentication for secure content management
                            </li>
                            <li>
                                <strong>SharePoint editorial workflow (SPFx)</strong> - Draft → Review → Double Peer Review → Publish
                            </li>
                            <li>
                                <strong>Automated search indexing</strong> - 5 Azure Functions syncing to Algolia with language-aware indexing
                            </li>
                            <li>
                                <strong>Multi-environment infrastructure</strong> (Prod/QA/Dev) with automated Sunday night Prod→QA sync
                            </li>
                            <li>
                                <strong>HIPAA-ready architecture</strong> with separated storage strategy for future PHI compliance
                            </li>
                            <li>
                                <strong>Database-driven localization</strong> - adding languages requires zero code changes
                            </li>
                        </ul>
                        <ViewCaseStudyButton href="/case-studies/medexplain">
                            View Detailed Case Study: AI-Powered Translation System →
                        </ViewCaseStudyButton>
                    </CaseSection>

                    <CaseSection>
                        <h3>Technical Scope</h3>
                        <TechStack>
                            <div className="tech">React</div>
                            <div className="tech">TypeScript</div>
                            <div className="tech">Node.js</div>
                            <div className="tech">Express</div>
                            <div className="tech">Azure Cosmos DB (3 environments)</div>
                            <div className="tech">Azure Functions (5+)</div>
                            <div className="tech">Azure AI Translator</div>
                            <div className="tech">Azure Blob Storage</div>
                            <div className="tech">Algolia Search</div>
                            <div className="tech">SharePoint SPFx</div>
                            <div className="tech">MSAL Authentication</div>
                            <div className="tech">CI/CD Pipelines</div>
                            <div className="tech">Cloudflare CDN</div>
                        </TechStack>
                    </CaseSection>

                    <CaseSection>
                        <h3>Results</h3>
                        <MetricsGrid>
                            <Metric>
                                <div className="number">2,000+</div>
                                <div className="label">Daily Users</div>
                            </Metric>
                            <Metric>
                                <div className="number">Weeks → Minutes</div>
                                <div className="label">Translation Time</div>
                            </Metric>
                            <Metric>
                                <div className="number">18</div>
                                <div className="label">Health Conditions</div>
                            </Metric>
                            <Metric>
                                <div className="number">Zero Code</div>
                                <div className="label">To Add Languages</div>
                            </Metric>
                        </MetricsGrid>
                        <p style={{ marginTop: '30px' }}>
                            Platform serves articles, journal summaries, videos, and quizzes - all with multilingual support
                            across English and Spanish. Comprehensive taxonomy system covering 18 health conditions, 12 health
                            behaviors, and 8 population groups.
                        </p>
                    </CaseSection>
                </CaseStudy>

                {/* ILITCH HOLDINGS */}
                <CaseStudy>
                    <CaseTitle>
                        <div className="client">Ilitch Holdings</div>
                        <h2>Enterprise M365 Transformation + Gateway Platform</h2>
                        <div className="role">Manager, Digital Applications | Oct 2017 - Aug 2020</div>
                    </CaseTitle>

                    <CaseSection>
                        <h3>The Challenge</h3>
                        <p>
                            Migrate 5,000+ users across 8 companies from IBM Notes to Office 365 while building new enterprise
                            portal for 4,000+ franchisees. Companies included Detroit Tigers (MLB), Detroit Red Wings (NHL),
                            Little Caesars (4,000+ franchise locations), Olympia Entertainment, and more.
                        </p>
                    </CaseSection>

                    <CaseSection>
                        <h3>The Solution</h3>
                        <p><strong>M365 Migration:</strong></p>
                        <ul>
                            <li>Led 8-company migration - one of Michigan's largest M365 transformations</li>
                            <li>Improved collaboration adoption 40%</li>
                            <li>Reduced Azure infrastructure costs 30% through automation and governance</li>
                            <li>Created company-wide O365 training videos during COVID-19</li>
                        </ul>

                        <p style={{ marginTop: '25px' }}><strong>Gateway Platform:</strong></p>
                        <ul>
                            <li>
                                Built enterprise application platform with customizable dashboards, real-time news distribution,
                                and urgent alert system (ticker tape for critical updates like food recalls)
                            </li>
                            <li>Deep ServiceNow integration for help desk access</li>
                            <li>Presented at national franchisee conferences in Las Vegas</li>
                            <li>Gateway authentication became core infrastructure leveraged by Caesar Vision and other major enterprise projects</li>
                            <li>Developed and delivered Gateway mobile app (Flutter) to Apple/Android stores</li>
                        </ul>

                        <p style={{ marginTop: '25px' }}><strong>Engineering Modernization:</strong></p>
                        <ul>
                            <li>
                                Introduced proper source control and DevOps practices - first team to implement CI/CD pipelines
                                company-wide, then helped other teams adopt
                            </li>
                            <li>Pioneered Agile/Scrum adoption, boosting delivery velocity 70%</li>
                            <li>Built and managed 5-year budget forecasts - only Manager-level executive to do so</li>
                        </ul>
                    </CaseSection>

                    <CaseSection>
                        <h3>Technical Scope</h3>
                        <TechStack>
                            <div className="tech">Office 365</div>
                            <div className="tech">Azure</div>
                            <div className="tech">React</div>
                            <div className="tech">Cosmos DB</div>
                            <div className="tech">Flutter</div>
                            <div className="tech">.NET</div>
                            <div className="tech">ServiceNow</div>
                            <div className="tech">IBM Notes</div>
                            <div className="tech">Agile/Scrum</div>
                            <div className="tech">CI/CD</div>
                            <div className="tech">DevOps</div>
                        </TechStack>
                    </CaseSection>

                    <CaseSection>
                        <h3>Recognition & Results</h3>
                        <MetricsGrid>
                            <Metric>
                                <div className="number">🏆</div>
                                <div className="label">IT Impact Award (2016)</div>
                            </Metric>
                            <Metric>
                                <div className="number">5,000+</div>
                                <div className="label">Users Migrated</div>
                            </Metric>
                            <Metric>
                                <div className="number">40%</div>
                                <div className="label">Collaboration Increase</div>
                            </Metric>
                            <Metric>
                                <div className="number">30%</div>
                                <div className="label">Azure Cost Reduction</div>
                            </Metric>
                            <Metric>
                                <div className="number">70%</div>
                                <div className="label">Velocity Improvement</div>
                            </Metric>
                            <Metric>
                                <div className="number">4,000+</div>
                                <div className="label">Franchisees Served</div>
                            </Metric>
                        </MetricsGrid>
                        <p style={{ marginTop: '30px' }}>
                            <strong>Award recognition:</strong> "Significant collaboration and business partnership in achieving
                            major process improvement for all IHI companies."
                        </p>
                        <p>
                            <strong>Leadership distinction:</strong> Only Manager-level (non-VP) executive presenting quarterly
                            to CEO via Executive Steering Committee.
                        </p>
                    </CaseSection>
                </CaseStudy>

                {/* WEILL CORNELL - PALS */}
                <CaseStudy>
                    <CaseTitle>
                        <div className="client">Weill Cornell Medicine</div>
                        <h2>PALS for Health Platform Stabilization</h2>
                        <div className="role">Technical Consultant | 2020-Present</div>
                    </CaseTitle>

                    <CaseSection>
                        <h3>The Challenge</h3>
                        <p>
                            Existing healthcare education platform (PALS) needed stabilization, performance improvements,
                            and infrastructure optimization to continue serving Weill Cornell's educational mission.
                        </p>
                    </CaseSection>

                    <CaseSection>
                        <h3>The Solution</h3>
                        <ul>
                            <li>Optimized Solr search performance - improved relevance and reduced downtime</li>
                            <li>Fixed AWS infrastructure issues for improved reliability</li>
                            <li>System maintenance and ongoing platform support</li>
                            <li>Ensured continued operation for educational content delivery</li>
                        </ul>
                    </CaseSection>

                    <CaseSection>
                        <h3>Results</h3>
                        <p>
                            Platform continues to serve Weill Cornell Medicine's healthcare education needs with improved
                            performance and reliability. Stabilization work ensured uninterrupted access to critical medical
                            education resources.
                        </p>
                    </CaseSection>
                </CaseStudy>
            </CaseStudiesSection> 

            {/* TESTIMONIALS */}
            <TestimonialsSection>
                <h2>Client Testimonials</h2>
                 
                <Testimonial>
                    <div className="quote">
                        Susan has created and implemented our entire technology stack at MedExplain Health, designing the
                        platform from the ground up, standing up a React front end with MSAL secured admin tools, a Cosmos DB
                        backend, Algolia search, and image handling in Azure Blob Storage. She has layered in automated backups,
                        Cloudflare performance tuning, and a CI/CD pipeline that lets our team push updates with confidence.

                        Susan has gone above and beyond, also building us a custom SharePoint workspace to aid in our content
                        creation workflow, mentoring interns working on our React Native prototype, and continuously refining
                        the user experience based on feedback from both our team and end users. Her mix of expert technical
                        skill, strategic vision, and her dedication to our mission has made her an ideal partner for
                        MedExplain Health.

                        I recommend Zumosoft without hesitation to anyone who needs an end-to-end technology partner who delivers
                        excellent, high-quality results.
                    </div>
                    <div className="author">— Ariel Barasch, Director of MedExplain Health</div>
                </Testimonial>
            </TestimonialsSection>
        </PageContainer>
    );
}

export default CaseStudies;